/*
 * Copyright (c) 2022 Robert J Whelton. All rights reserved.
 */

import * as React from "react"
import CheckIcon from "@mui/icons-material/Check"
import CloseIcon from "@mui/icons-material/Close"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Grid from "@mui/material/Grid"
import IconButton from "@mui/material/IconButton"
import InputAdornment from "@mui/material/InputAdornment"
import FormControl from "@mui/material/FormControl"
import Typography from "@mui/material/Typography"
import {OutlinedInput, InputLabel} from "@mui/material"

import {ToggleFunctionType} from "../layouts/Layout"
import BaseDrawer, {standardWidthFactor} from "./BaseDrawer"


interface GridItemType {
  text: string
  icon: any
  divider?: boolean
  item?: any
}

interface GridSelectDrawerProps {
  title: string
  subtitle: string
  anchor: "top" | "bottom" | "left" | "right"
  open: boolean
  toggleDrawer: ToggleFunctionType
  items: GridItemType[]
  onSelect: (selectedItem: string, textInput: string) => boolean  // returns true if form processing complete and can close
  inputLabel: string
  inputProps: object
  inputId?: string,
  setTextOnSelection?: boolean
  focusTextOnSelection?: boolean
  actionItemIcon?: any
  altActionItemIcon?: any
  altInputLabel?: string
}


const GridSelectDrawer: React.FC<GridSelectDrawerProps> = ({
                                                             title,
                                                             subtitle,
                                                             anchor,
                                                             open,
                                                             toggleDrawer,
                                                             items,
                                                             onSelect,
                                                             inputLabel,
                                                             inputProps,
                                                             inputId = "grid-select-drawer-input",
                                                             setTextOnSelection = false,
                                                             focusTextOnSelection = false,
                                                             actionItemIcon = <CheckIcon/>,
                                                             altActionItemIcon = undefined,
                                                             altInputLabel = "",
                                                           }) => {
  const [selected, setSelected] = React.useState("")
  const [textInput, setTextInput] = React.useState("")
  const [activeActionItemIcon, setActiveActionItemIcon] = React.useState(altActionItemIcon || actionItemIcon)
  const [activeInputLabel, setActiveInputLabel] = React.useState(altInputLabel || inputLabel)
  const inputRef = React.useRef(null)
  // force uniqueness of items
  items = Array.from(new Map(items.filter(item => !textInput || item.text.toLowerCase().includes(textInput.toLowerCase())).map(item => [item.text, item])).values())

  const closeMenu = () => {
    setTimeout(() => {
      setSelected("")
      setTextInput("")
      setActiveActionItemIcon(altActionItemIcon || actionItemIcon)
      setActiveInputLabel(altInputLabel || inputLabel)
    }, 1000)  // as toggled: !open === closed  when open = true
    toggleDrawer(true)()
  }

  const captureEscapeKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault()
      closeMenu()
    }
  }

  const itemSelectedAction = (e: React.SyntheticEvent) => {
    e.preventDefault()
    if (selected || textInput) {
      if (onSelect(selected, textInput)) closeMenu()
    }
    inputRef.current.focus()
  }

  const textInputChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const text = (e.target as HTMLInputElement).value
    if (text && (selected !== text)) {
      setActiveActionItemIcon(altActionItemIcon || actionItemIcon)
      setActiveInputLabel(altInputLabel || inputLabel)
    }
    setTextInput(text)
  }

  const gridItemClicked = (e: React.SyntheticEvent, text: string) => {
    e.preventDefault()
    let label = inputLabel
    if (focusTextOnSelection) inputRef.current.focus()
    if (selected === text) {  // UN-select
      setTextInput(text = "")
      label = altInputLabel
    }
    if (text && setTextOnSelection) setTextInput(text)
    setActiveActionItemIcon(actionItemIcon)
    setActiveInputLabel(label)
    setSelected(text)
  }

  return (
    <BaseDrawer open={open} toggleDrawer={toggleDrawer} anchor={anchor}>
      <Typography variant={"h6"} mx={2} my={1} onClick={() => inputRef.current.focus()}>
        {title}
      </Typography>
      <Box
        component="form"
        sx={{p: "0 2px", display: "flex", alignItems: "center", width: "100%"}}
        onSubmit={(e) => itemSelectedAction(e)}
        onKeyDown={captureEscapeKey}
      >
        <FormControl sx={{mx: 1, width: "100%"}} variant="outlined">
          <InputLabel htmlFor={inputId}>{activeInputLabel}</InputLabel>
          <OutlinedInput
            id={inputId}
            size="small"
            inputRef={inputRef}
            autoFocus
            label={activeInputLabel}
            value={textInput}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  color="success"
                  onClick={(e) => itemSelectedAction(e)}
                >{activeActionItemIcon}</IconButton>
                <IconButton color="error" onClick={closeMenu}><CloseIcon/></IconButton>
              </InputAdornment>
            }
            sx={{p: 0}}
            inputProps={inputProps}
            onChange={(e) => textInputChange(e)}
          />
        </FormControl>
      </Box>
      <Typography variant={"subtitle2"} mx={2} mt={1} mb={0}>
        {subtitle}
      </Typography>
      <Box
        sx={{flexGrow: 1, padding: 1, overflow: "scroll"}}
        mb={4}
        onTouchStart={() => inputRef.current.blur()}
      >
        <Grid container spacing={1}>
          {items.map(({text, icon}) => (
            <Grid item key={text}>
              <Button
                variant={selected === text ? "contained" : "outlined"}
                onClick={(e) => gridItemClicked(e, text)}
                startIcon={icon}
              >{text}</Button>
            </Grid>
          ))}
        </Grid>
      </Box>
    </BaseDrawer>
  )
}

export default GridSelectDrawer
